import React from 'react';
import {FormProvider, useFieldArray, useForm, useFormContext} from 'react-hook-form';
import type {ProyectoFormDTO} from '../../../../api/Objetos';
import {plazosApi} from '../../../../api/api';
import {createEmptyProyecto} from '../emptyProyecto';
import {DateField, FieldGrid, NumberField, TextAreaField, TextField, CurrencyField, ToggleField} from '../../../../ui/form/fields';
import {
    EmptyState,
    FormSection,
    ItemCard,
    buildCardTitle,
    buildSummary,
    formatSummaryNumber,
    hasPersistedId,
    useProjectReadOnly,
    BulkToggle
} from '../projectForm.helpers';
import {SectionAlerts, SectionSaveButton, useCollectionSection} from '../sectionCrudUtils';
import {useRegisterSectionSave} from '../SectionSaveCoordinator';
import BarraCargando from "../../../../ui/BarraCargando";
import { useTranslation } from 'react-i18next';

export function PlazosSection(props: { proyectoId: number; showIds?: boolean }) {
    const readOnly = useProjectReadOnly();
    const form = useForm<ProyectoFormDTO>({defaultValues: createEmptyProyecto()});
    const section = useCollectionSection({
        queryKey: ['admin', 'proyectos', 'plazos'],
        proyectoId: props.proyectoId,
        getFn: plazosApi.list,
        createFn: plazosApi.create,
        updateFn: plazosApi.update,
        deleteFn: plazosApi.delete,
    });

    React.useEffect(() => {
        form.reset({...createEmptyProyecto(), plazosEjecucionJustificacion: section.data ?? []});
    }, [form, section.data]);

    const handleSave = form.handleSubmit(async (value) => {
        await section.save(value.plazosEjecucionJustificacion ?? []);
    });

    useRegisterSectionSave({
        id: 'plazos',
        isDirty: form.formState.isDirty,
        isSaving: section.saveMutation.isPending,
        save: async () => {
            await handleSave();
        },
    });

    return (
        <FormProvider {...form}>
            <PlazosSectionFields 
                showIds={Boolean(props.showIds)}
                isLoading={section.isLoading}
                loadError={section.error}
            />
            {!readOnly && (
                <>
                    <SectionSaveButton onClick={() => void handleSave()} busy={section.saveMutation.isPending}/>
                </>
            )}
        </FormProvider>
    );
}

function PlazosSectionFields(props: { showIds: boolean, isLoading: boolean, loadError: any }) {
    const { t } = useTranslation();
    const form = useFormContext<ProyectoFormDTO>();
    const plazos = useFieldArray({control: form.control, name: 'plazosEjecucionJustificacion'});
    const showId = (path: string) => props.showIds && hasPersistedId(form.watch(path as never));
    const readOnly = useProjectReadOnly();

    const setAllPublic = (isPublic: boolean) => {
        plazos.fields.forEach((field, index) => {
            form.setValue(`plazosEjecucionJustificacion.${index}.publico`, isPublic, { shouldDirty: true });
        });
    };

    const currentPlazos = form.watch('plazosEjecucionJustificacion') || [];

    return (
        <FormSection
            id="plazos"
            title={t('plazosSection.title', { count: plazos.fields.length })}
            collapsible
            defaultOpen={false}
            headerActions={!readOnly &&
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                    {props.isLoading && <BarraCargando size="1.2em" />}
                    <BulkToggle
                        label={t('plazosSection.public')}
                        fields={currentPlazos}
                        onToggle={setAllPublic}
                    />
                </div>
            }
        >
            <SectionAlerts loadError={props.loadError}/>
            {plazos.fields.length === 0 && <EmptyState message={t('plazosSection.noDeadlines')}/>}
            {plazos.fields.map((field, index) => (
                <ItemCard
                    key={field.id}
                    title={buildCardTitle(
                        t,
                        t('plazosSection.deadline'),
                        index,
                        form.watch(`plazosEjecucionJustificacion.${index}.convocatoria`),
                        form.watch(`plazosEjecucionJustificacion.${index}.auditorias`),
                        form.watch(`plazosEjecucionJustificacion.${index}.observaciones`)
                    )}
                    summary={buildSummary(
                        t,
                        form.watch(`plazosEjecucionJustificacion.${index}.convocatoria`),
                        formatSummaryNumber(form.watch(`plazosEjecucionJustificacion.${index}.importeSubvencion`)),
                        form.watch(`plazosEjecucionJustificacion.${index}.plazoMaximoEjecucion`)
                    )}
                    onRemove={() => plazos.remove(index)}
                    defaultOpen={!hasPersistedId(form.watch(`plazosEjecucionJustificacion.${index}.id` as never))}
                    actions={!readOnly &&
                        <ToggleField
                            label={t('plazosSection.public')}
                            name={`plazosEjecucionJustificacion.${index}.publico`}
                            register={form.register}
                        />
                    }
                >
                    <FieldGrid>
                        {showId(`plazosEjecucionJustificacion.${index}.id`) ?
                            <NumberField label={t('plazosSection.id')} name={`plazosEjecucionJustificacion.${index}.id`}
                                         register={form.register}/> : null}
                        <TextField label={t('plazosSection.call')} name={`plazosEjecucionJustificacion.${index}.convocatoria`}
                                   register={form.register}/>

                        <CurrencyField label={t('plazosSection.totalProjectAmount')}
                                       name={`plazosEjecucionJustificacion.${index}.importeTotalProyecto`}
                                       register={form.register} />

                        <CurrencyField label={t('plazosSection.grantAmount')}
                                       name={`plazosEjecucionJustificacion.${index}.importeSubvencion`}
                                       register={form.register} />

                        <DateField label={t('plazosSection.maxExecutionDeadline')}
                                   name={`plazosEjecucionJustificacion.${index}.plazoMaximoEjecucion`}
                                   register={form.register}/>
                        <DateField label={t('plazosSection.maxJustificationDeadline')}
                                   name={`plazosEjecucionJustificacion.${index}.plazoMaximoJustificacion`}
                                   register={form.register}/>
                        <DateField label={t('plazosSection.extendedExecutionDeadline')}
                                   name={`plazosEjecucionJustificacion.${index}.plazoAmpliadoEjecucion`}
                                   register={form.register}/>
                        <DateField label={t('plazosSection.extendedJustificationDeadline')}
                                   name={`plazosEjecucionJustificacion.${index}.plazoAmpliadoJustificacion`}
                                   register={form.register}/>
                        <TextField label={t('plazosSection.audits')} name={`plazosEjecucionJustificacion.${index}.auditorias`}
                                   register={form.register}/>
                    </FieldGrid>
                    <TextAreaField label={t('plazosSection.observations')} name={`plazosEjecucionJustificacion.${index}.observaciones`}
                                   register={form.register}/>
                </ItemCard>
            ))}

            {!readOnly ? (
                <button
                    className="btn btnSecondary"
                    type="button"
                    onClick={() =>
                        plazos.append({
                            id: 0,
                            convocatoria: '',
                            importeTotalProyecto: 0,
                            importeSubvencion: 0,
                            plazoMaximoEjecucion: '',
                            plazoMaximoJustificacion: '',
                            plazoAmpliadoEjecucion: '',
                            plazoAmpliadoJustificacion: '',
                            auditorias: '',
                            observaciones: '',
                            publico: false,
                        })
                    }
                >
                    {t('plazosSection.addDeadline')}
                </button>
            ) : null}
        </FormSection>
    );
}
